export const ACTIVITY_TYPES = {
  CONTRIBUTIONS: 'Contributions',
  DISTRIBUTIONS: 'Distributions',
  DIVIDENDS_AND_INTEREST: 'Dividends and Interest',
  DIVIDEND_INTEREST_REINVESTED: 'Dividend Interest Reinvested',
  ADVISORY_FEES_PAID: 'Advisory Fees Paid',
  MISCELLANEOUS_CHARGES: 'Miscellaneous Charges',
  EXCHANGE_IN: 'Exchange In',
  EXCHANGE_OUT: 'Exchange Out',
  JOURNAL_IN: 'Journal In',
  JOURNAL_OUT: 'Journal Out',
  MERGE_IN: 'Merge In',
  MERGE_OUT: 'Merge Out',
  NET_DIV_INT_GAINS: 'Net Dividends/Interest/Gains Withdrawn'
}

export const MASTER_ROWS = [
  {
    key: 'beginning-market-value',
    name: 'Beginning Market Value',
    rowType: 'valuation',
    valuationMethod: 'Beginning Market Value'
  },
  {
    key: 'beginning-bond-accrual',
    name: 'Beginning Bond Accrual',
    rowType: 'valuation',
    valuationMethod: 'Beginning Market Value',
    includeAccruedInterest: true
  },
  {
    key: 'contributions',
    name: 'Contributions',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.CONTRIBUTIONS]
  },
  {
    key: 'distributions',
    name: 'Distributions',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.DISTRIBUTIONS]
  },
  {
    key: 'dividends-interest',
    name: 'Dividends & Interest',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.DIVIDENDS_AND_INTEREST]
  },
  {
    key: 'reinvested-income',
    name: 'Reinvested Income',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.DIVIDEND_INTEREST_REINVESTED]
  },
  {
    key: 'advisory-fees',
    name: 'Advisory Fees',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.ADVISORY_FEES_PAID]
  },
  {
    key: 'misc-charges',
    name: 'Miscellaneous Charges',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.MISCELLANEOUS_CHARGES]
  },
  {
    key: 'exchanges-in',
    name: 'Exchanges In',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.EXCHANGE_IN]
  },
  {
    key: 'exchanges-out',
    name: 'Exchanges Out',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.EXCHANGE_OUT]
  },
  {
    key: 'journal-in',
    name: 'Journal In',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.JOURNAL_IN]
  },
  {
    key: 'journal-out',
    name: 'Journal Out',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.JOURNAL_OUT]
  },
  {
    key: 'merge-in',
    name: 'Merge In',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.MERGE_IN]
  },
  {
    key: 'merge-out',
    name: 'Merge Out',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.MERGE_OUT]
  },
  {
    key: 'net-div-int-gains',
    name: 'Net Dividends/Interest/Gains Withdrawn',
    rowType: 'activity',
    activityTypes: [ACTIVITY_TYPES.NET_DIV_INT_GAINS]
  },
  {
    key: 'mv-increase-decrease',
    name: 'Market Value Increase/Decrease',
    rowType: 'derived',
    derivation: 'mvChange',
    includedActivityKeys: null
  },
  {
    key: 'ending-bond-accrual',
    name: 'Ending Bond Accrual',
    rowType: 'valuation',
    valuationMethod: 'Ending Market Value',
    includeAccruedInterest: true
  },
  {
    key: 'ending-market-value',
    name: 'Ending Market Value',
    rowType: 'valuation',
    valuationMethod: 'Ending Market Value'
  },
  {
    key: 'performance-twr',
    name: 'TWR',
    rowType: 'performance',
    method: 'TWR'
  },
  {
    key: 'performance-irr',
    name: 'IRR',
    rowType: 'performance',
    method: 'IRR'
  }
]

const STRUCTURAL_KEYS = new Set([
  'beginning-market-value', 'ending-market-value', 'mv-increase-decrease'
])

const STANDARD_ENABLED = new Set([
  ...STRUCTURAL_KEYS,
  'contributions', 'distributions', 'advisory-fees'
])

const STANDARD_OVERRIDES = {
  'dividends-interest': {
    name: 'Income',
    activityTypes: [ACTIVITY_TYPES.DIVIDENDS_AND_INTEREST, ACTIVITY_TYPES.DIVIDEND_INTEREST_REINVESTED]
  }
}

const DETAILED_ENABLED = new Set([
  ...STRUCTURAL_KEYS,
  'contributions', 'distributions', 'dividends-interest', 'reinvested-income',
  'advisory-fees', 'misc-charges', 'exchanges-in', 'exchanges-out',
  'journal-in', 'journal-out'
])

function buildPresetRows (enabledKeys, overrides = {}) {
  return MASTER_ROWS.map(row => {
    const ov = overrides[row.key]
    return {
      ...row,
      ...(ov || {}),
      enabled: enabledKeys.has(row.key) || !!ov
    }
  })
}

export const ROW_PRESETS = {
  standard: () => buildPresetRows(STANDARD_ENABLED, STANDARD_OVERRIDES),
  detailed: () => buildPresetRows(DETAILED_ENABLED)
}

export function buildDefaultRows (presetKey) {
  const factory = ROW_PRESETS[presetKey]
  return factory ? factory() : ROW_PRESETS.standard()
}

export function resolveRows (config) {
  const rows = config.rows ?? config.buckets
  if (Array.isArray(rows) && rows.length) {
    return rows.filter(r => r.enabled !== false)
  }
  const presetKey = config.rowPreset ?? config.bucketPreset ?? 'standard'
  return buildDefaultRows(presetKey).filter(r => r.enabled !== false)
}
